import React, { useEffect } from "react";
import { api } from "../../../services/api";
import { getUser, setUser } from "../../../services/utils";
import { CONTACT_FETCH_INTERVAL } from "../../mailbox/context/mailbox-context";

const ACTIVE_STATUS_INTERVAL = 1000 * 60; // 1 minute

interface UserContextType {
  userInfo: any;
  refreshUserInfo: () => {};
  setUserInfo: any;
  fetchUser: any;
  loading: any;
  selectedProvider: any;
  setSelectedProvider: any;
  fetchPatientsCount: any;
  patientCount: any;
  unreadPatientConversationCount: any;
}

export const UserContext = React.createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }) {
  const [userInfo, setUserInfo] = React.useState({});
  const [userInfoLoading, setUserInfoLoading] = React.useState(false);
  const [patientCount, setPatientCount] = React.useState(0);
  const [unreadPatientConversationCount, setUnreadPatientConversationCount] = React.useState(0);
  const [selectedProvider, setSelectedProvider] = React.useState<any>(null);
  const user = getUser();
  const activeStatusRef = React.useRef<any>(null);
  const patientCountRef = React.useRef<any>(null);

  const fetchUser = async (userId: string, user?: any) => {
    try {
      setUserInfoLoading(true);
      const response = await api.getUserProfile(userId);
      setUserInfoLoading(false);
      setUserInfo({
        ...response,
      });
      setUser({
        ...(user || {}),
        ...response,
      });
    } catch (error) { }
  };

  const fetchPatientsCount = async () => {
    try {
      const response = await api.getPatientsCount(user?.id);
      if ([200, 201].includes(response.status)) {
        setPatientCount(response.data.count);
      }
    } catch (error) { }
    try {
      const convRes = await api.getAllUnreadPatientConversationCount(user?.id);
      if ([200, 201].includes(convRes.status)) {
        setUnreadPatientConversationCount(convRes.data.count);
      }
    } catch (error) { }
  };

  const refreshUserInfo = async () => {
    return await fetchUser(user?.id, user);
  }

  const values = {
    userInfo,
    refreshUserInfo,
    setUserInfo,
    fetchUser,
    loading: userInfoLoading,
    selectedProvider,
    setSelectedProvider,
    fetchPatientsCount,
    patientCount,
    unreadPatientConversationCount,
  };
  useEffect(() => {
    if (user?.id) {
      fetchUser(user?.id, user);

      if (activeStatusRef.current) clearInterval(activeStatusRef.current);
      activeStatusRef.current = setInterval(() => {
        api.markUserAsActive(user?.id);
      }, ACTIVE_STATUS_INTERVAL);

      if (user?.type === "P") {
        fetchPatientsCount();
        if (patientCountRef.current) clearInterval(patientCountRef.current);
        patientCountRef.current = setInterval(() => {
          fetchPatientsCount();
        }, CONTACT_FETCH_INTERVAL);
      }
    }
    return () => {
      if (activeStatusRef.current) clearInterval(activeStatusRef.current);
      if (patientCountRef.current) clearInterval(patientCountRef.current);
    };
  }, [user?.id]);
  return <UserContext.Provider value={values}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
